import React, { useEffect, useState } from 'react';
import { AppSidebar } from '@/components/app-sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import {
  ScoreActionConfig,
  getScoreActionConfigs,
  updateScoreActionPoints,
  initializeScoreConfiguration,
  UserScore,
  calculateAllUserScores,
  devResetWeeklyScores,
  devResetMonthlyScores,
  devAdjustWeek,
  devResetHighScores,
  devRecalculateAllScores
} from '@/services/scoreService';
import { useToast } from '@/components/ui/use-toast';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Award, Crown, Star, Pencil, Trash2, PlusCircle, Gift, Trophy, Coffee, Film, ShoppingCart, CalendarPlus, PartyPopper, Zap, HelpCircle, type LucideIcon } from 'lucide-react';
import { User, getPraktikanten } from '@/services/userService';
import { fetchLeadsForCurrentUser } from "@/services/leadsService";
import { Reward, getRewards, addReward, updateReward, deleteReward, NewReward } from '@/services/rewardService';
import { ManageRewardsDialog } from '@/components/ManageRewardsDialog';
import { logger } from "@/lib/logger";
import { format, addWeeks, subWeeks, startOfDay, subDays, addDays } from 'date-fns';
import { de } from 'date-fns/locale';

// Define structure for Rewards (Prämien) - to be managed via Firestore later
// interface Reward { // Interface is now imported from rewardService
//   id: string;
//   name: string;
//   pointsRequired: number;
//   imageUrl?: string; // Optional image for the reward
// }

// Placeholder for rewards data - this will also become dynamic
// const placeholderRewards: Reward[] = [
//   { id: "1", name: "Kinogutschein", pointsRequired: 50 },
//   { id: "2", name: "Amazon Gutschein 25€", pointsRequired: 100 },
//   { id: "3", name: "Ein Tag extra Urlaub", pointsRequired: 250 },
// ];

// Mapping from iconName to Lucide Icon Component
const iconMap: { [key: string]: LucideIcon } = {
  Gift,
  Star,
  Award,
  Trophy,
  Coffee,
  Film,
  ShoppingCart,
  CalendarPlus,
  PartyPopper,
  Zap,
};

const DefaultRewardIcon = HelpCircle; // Fallback icon

export default function ScoreBoardPage() {
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const [scoreConfigs, setScoreConfigs] = useState<ScoreActionConfig[]>([]);
  const [editablePoints, setEditablePoints] = useState<{ [key: string]: string }>({});
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [leaderboardData, setLeaderboardData] = useState<UserScore[]>([]);
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(true);

  const [rewards, setRewards] = useState<Reward[]>([]); // State for rewards
  const [isLoadingRewards, setIsLoadingRewards] = useState(true); // State for loading rewards

  // States for managing the rewards dialog
  const [isRewardDialogOpen, setIsRewardDialogOpen] = useState(false);
  const [editingReward, setEditingReward] = useState<Reward | null>(null);

  const [isResettingScores, setIsResettingScores] = useState(false);
  const [isAdjustingWeek, setIsAdjustingWeek] = useState(false);
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0);

  const adminOnlyActionNames = [
    "Lead erhält Status 'Neu'",
    "Lead erhält Status 'Terminiert' (Scheduled)",
    "Lead wechselt von 'Terminiert' zu 'Wiedervorlage' (Follow-up)",
    "Lead erhält Status 'Gewonnen' (Won)",
    "Spezieller Recruiting-Kontakt wird erfasst"
  ];

  // Calculate the current week's date range for display and scoring
  const getCurrentWeekRange = () => {
    const now = new Date();
    const adjustedDate = addWeeks(now, currentWeekOffset);
    const startOfWeek = startOfDay(subDays(adjustedDate, adjustedDate.getDay()));
    const endOfWeek = addDays(startOfWeek, 6);
    return {
      start: format(startOfWeek, 'dd.MM.yyyy', { locale: de }),
      end: format(endOfWeek, 'dd.MM.yyyy', { locale: de }),
      startDate: startOfWeek // Add this to pass to calculateAllUserScores
    };
  };

  const weekRange = getCurrentWeekRange();

  // Function to reload all data
  const reloadData = async () => {
    logger.log("ScoreBoardPage: reloadData started.");
    setIsLoadingConfigs(true);
    setIsLoadingLeaderboard(true);
    setIsLoadingRewards(true);

    try {
      if (currentUser?.role === 'admin') {
        await initializeScoreConfiguration();
      }

      const configs = await getScoreActionConfigs();
      setScoreConfigs(configs);

      const initialEditablePoints: { [key: string]: string } = {};
      configs.forEach(config => {
        initialEditablePoints[config.id] = config.points.toString();
      });
      setEditablePoints(initialEditablePoints);

      const praktikanten = await getPraktikanten(currentUser.tenantId);
      // Fetch leads based on user role and tenant
      const allLeads = await fetchLeadsForCurrentUser(
        currentUser.id,
        currentUser.role,
        currentUser.tenantId
      );

      if (praktikanten.length > 0 && configs.length > 0) {
        // Pass the selected date to calculateAllUserScores
        const scores = await calculateAllUserScores(
          praktikanten,
          allLeads,
          configs,
          "praktikant",
          weekRange.startDate, // Pass the selected date
          currentUser.tenantId
        );
        setLeaderboardData(scores);
      } else {
        setLeaderboardData([]);
      }

      const fetchedRewards = await getRewards();
      setRewards(fetchedRewards);
    } catch (error) {
      logger.error("ScoreBoardPage: Error in reloadData:", error);
      setLeaderboardData([]);
      setScoreConfigs([]);
      setRewards([]);
    } finally {
      setIsLoadingConfigs(false);
      setIsLoadingLeaderboard(false);
      setIsLoadingRewards(false);
    }
  };

  // Replace the existing useEffect with this one
  useEffect(() => {
    if (currentUser) {
      reloadData();
    } else {
      setIsLoadingConfigs(false);
      setIsLoadingLeaderboard(false);
      setIsLoadingRewards(false);
      setLeaderboardData([]);
      setScoreConfigs([]);
      setRewards([]);
    }
  }, [currentUser]);

  const handlePointChange = (actionId: string, value: string) => {
    setEditablePoints(prev => ({ ...prev, [actionId]: value }));
  };

  // --- Reward Management Handlers ---
  const openAddRewardDialog = () => {
    setEditingReward(null);
    setIsRewardDialogOpen(true);
  };

  const openEditRewardDialog = (reward: Reward) => {
    setEditingReward(reward);
    setIsRewardDialogOpen(true);
  };

  const closeRewardDialog = () => {
    setIsRewardDialogOpen(false);
    setEditingReward(null);
  };

  const handleRewardSubmit = async (rewardData: NewReward | Reward, isEditing: boolean) => {
    try {
      if (isEditing && 'id' in rewardData) {
        await updateReward(rewardData.id, rewardData as Partial<NewReward>); // Cast for update
        toast({ title: "Erfolg", description: "Prämie erfolgreich aktualisiert." });
      } else {
        await addReward(rewardData as NewReward); // Cast for add
        toast({ title: "Erfolg", description: "Prämie erfolgreich hinzugefügt." });
      }
      await reloadData(); // Refresh the rewards list
      // Dialog will be closed by itself if onSubmit is successful in ManageRewardsDialog
    } catch (error) {
      console.error("Error submitting reward:", error);
      toast({ title: "Fehler", description: `Prämie konnte nicht ${isEditing ? 'aktualisiert' : 'hinzugefügt'} werden.`, variant: "destructive" });
      throw error; // Re-throw to let dialog know submission failed
    }
  };

  const handleRewardDelete = async (rewardId: string) => {
    // Optional: Add a confirmation dialog here before deleting
    if (!window.confirm("Sind Sie sicher, dass Sie diese Prämie löschen möchten?")) {
      return;
    }
    try {
      await deleteReward(rewardId);
      toast({ title: "Erfolg", description: "Prämie erfolgreich gelöscht." });
      await reloadData(); // Refresh the rewards list
    } catch (error) {
      console.error("Error deleting reward:", error);
      toast({ title: "Fehler", description: "Prämie konnte nicht gelöscht werden.", variant: "destructive" });
    }
  };
  // --- End Reward Management Handlers ---

  const handleSaveChanges = async () => {
    if (currentUser?.role !== 'admin' && currentUser?.role !== 'developer') {
      toast({ title: "Fehler", description: "Nur Admins und Entwickler können Punkteinstellungen speichern.", variant: "destructive" });
      return;
    }
    setIsSaving(true);
    let changesMade = 0;
    let allSuccessful = true;

    const currentConfigs = await getScoreActionConfigs();

    for (const config of currentConfigs) {
      const newPointsStr = editablePoints[config.id];
      if (newPointsStr === undefined) continue;

      const newPoints = parseInt(newPointsStr, 10);

      if (isNaN(newPoints) || newPoints < 0) {
        toast({
          title: "Ungültige Eingabe",
          description: `Bitte geben Sie eine gültige, nicht-negative Zahl für "${config.actionName}" ein.`,
          variant: "destructive",
        });
        allSuccessful = false;
        continue;
      }

      if (newPoints !== config.points) {
        const success = await updateScoreActionPoints(config.id, newPoints);
        if (success) {
          changesMade++;
        } else {
          allSuccessful = false;
          toast({
            title: "Fehler beim Speichern",
            description: `Punkte für "${config.actionName}" konnten nicht aktualisiert werden.`,
            variant: "destructive",
          });
        }
      }
    }
    setIsSaving(false);
    if (changesMade > 0 && allSuccessful) {
      toast({ title: "Gespeichert", description: "Punkteinstellungen erfolgreich aktualisiert." });
      await reloadData();
    } else if (changesMade === 0 && allSuccessful) {
      toast({ title: "Keine Änderungen", description: "Es gab keine Änderungen zum Speichern." });
    }
  };

  const handleDevResetWeekly = async () => {
    if (currentUser?.role !== 'admin') return;

    setIsResettingScores(true);
    try {
      await devResetWeeklyScores();
      toast({
        title: "Wochenpunkte zurückgesetzt",
        description: "Die Wochenpunkte wurden erfolgreich zurückgesetzt.",
      });
      // Reload the leaderboard data
      await reloadData();
    } catch (error) {
      console.error("Error resetting weekly scores:", error);
      toast({
        title: "Fehler",
        description: "Die Wochenpunkte konnten nicht zurückgesetzt werden.",
        variant: "destructive"
      });
    } finally {
      setIsResettingScores(false);
    }
  };

  const handleDevResetMonthly = async () => {
    if (currentUser?.role !== 'admin') return;

    setIsResettingScores(true);
    try {
      await devResetMonthlyScores();
      toast({
        title: "Monatspunkte zurückgesetzt",
        description: "Die Monatspunkte wurden erfolgreich zurückgesetzt.",
      });
      // Reload the leaderboard data
      await reloadData();
    } catch (error) {
      console.error("Error resetting monthly scores:", error);
      toast({
        title: "Fehler",
        description: "Die Monatspunkte konnten nicht zurückgesetzt werden.",
        variant: "destructive"
      });
    } finally {
      setIsResettingScores(false);
    }
  };

  const handleDevResetHighScores = async () => {
    if (currentUser?.role !== 'developer') return;

    if (!window.confirm("WARNUNG: Dies setzt alle High-Scores zurück und kann nicht rückgängig gemacht werden. Fortfahren?")) {
      return;
    }

    setIsResettingScores(true);
    try {
      await devResetHighScores();
      toast({
        title: "High-Scores zurückgesetzt",
        description: "Alle High-Scores wurden erfolgreich zurückgesetzt.",
      });
      // Reload the leaderboard data
      await reloadData();
    } catch (error) {
      console.error("Error resetting high scores:", error);
      toast({
        title: "Fehler",
        description: "Die High-Scores konnten nicht zurückgesetzt werden.",
        variant: "destructive"
      });
    } finally {
      setIsResettingScores(false);
    }
  };

  const handleDevRecalculateAll = async () => {
    if (currentUser?.role !== 'developer') return;

    if (!window.confirm("KOMPLETT-NEUBERECHNUNG: Dies berechnet alle Scores über den gesamten Zeitraum neu. Fortfahren?")) {
      return;
    }

    setIsResettingScores(true);
    try {
      await devRecalculateAllScores();
      toast({
        title: "Scores neu berechnet",
        description: "Alle Scores wurden über den gesamten Zeitraum neu berechnet.",
      });
      // Reload the leaderboard data
      await reloadData();
    } catch (error) {
      console.error("Error recalculating scores:", error);
      toast({
        title: "Fehler",
        description: "Die Neuberechnung ist fehlgeschlagen.",
        variant: "destructive"
      });
    } finally {
      setIsResettingScores(false);
    }
  };

  const handleAdjustWeek = async (weeksToAdd: number) => {
    if (currentUser?.role !== 'admin') return;

    setIsAdjustingWeek(true);
    try {
      await devAdjustWeek(weeksToAdd);
      setCurrentWeekOffset(prev => prev + weeksToAdd);
      toast({
        title: "Woche angepasst",
        description: `Die Woche wurde um ${weeksToAdd} Wochen ${weeksToAdd > 0 ? 'vor' : 'zurück'} verschoben.`,
      });
      await reloadData();
    } catch (error) {
      console.error("Error adjusting week:", error);
      toast({
        title: "Fehler",
        description: "Die Woche konnte nicht angepasst werden.",
        variant: "destructive"
      });
    } finally {
      setIsAdjustingWeek(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <AppSidebar />
      <div className="flex-1 overflow-y-auto p-6">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Scoreboard</h1>
            <p className="text-muted-foreground">
              Übersicht der Leistungen und erreichter Prämien.
            </p>
          </div>
          {currentUser?.role === 'admin' && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-md border">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleAdjustWeek(-1)}
                  disabled={isAdjustingWeek}
                  className="h-8 w-8 p-0"
                >
                  ←
                </Button>
                <span className="text-sm font-medium">
                  {weekRange.start} - {weekRange.end}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleAdjustWeek(1)}
                  disabled={isAdjustingWeek}
                  className="h-8 w-8 p-0"
                >
                  →
                </Button>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setCurrentWeekOffset(0);
                  handleAdjustWeek(0);
                }}
                disabled={isAdjustingWeek || currentWeekOffset === 0}
                className="text-xs"
              >
                Zurück zu aktueller Woche
              </Button>
            </div>
          )}
          {currentUser?.role === 'developer' && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDevRecalculateAll}
                disabled={isResettingScores}
                className="text-xs bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
              >
                {isResettingScores ? "Berechnet..." : "Scores Neuberechnen"}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDevResetHighScores}
                disabled={isResettingScores}
                className="text-xs"
              >
                {isResettingScores ? "Resettet..." : "High-Scores Reset"}
              </Button>
            </div>
          )}
        </div>

        {/* Adjusted Main Grid: Punktesystem and Prämien will be side-by-side or stacked on smaller screens */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Column 1: Score Point System */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Star className="mr-2 h-5 w-5 text-yellow-500" />
                Punktesystem
              </CardTitle>
              <CardDescription>
                Definierte Aktionen und ihre aktuellen Punktwerte.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingConfigs ? (
                <p>Lade Punktesystem...</p>
              ) : scoreConfigs.length === 0 ? (
                <p>Keine Punktesystem-Konfiguration gefunden.</p> // Simpler message
              ) : (
                <div className="space-y-3">
                  {scoreConfigs.map((item) => (
                    (currentUser?.role === 'admin' || !adminOnlyActionNames.includes(item.actionName)) && (
                      <div key={item.id} className="flex justify-between items-center p-2 bg-gray-50 rounded-md">
                        <div>
                          <p className="font-semibold text-gray-700">{item.actionName}</p>
                          {currentUser?.role === 'admin' && (
                            <p className="text-xs text-gray-500">{item.details}</p>
                          )}
                        </div>
                        {currentUser?.role === 'admin' ? (
                          <Input
                            type="number"
                            value={editablePoints[item.id] || ''}
                            onChange={(e) => handlePointChange(item.id, e.target.value)}
                            className="w-20 text-right"
                            min="0"
                          />
                        ) : (
                          <span className="font-bold text-lg text-indigo-600">{item.points}</span>
                        )}
                      </div>
                    )
                  ))}
                  {currentUser?.role === 'admin' && (
                    <Button onClick={handleSaveChanges} disabled={isSaving || isLoadingConfigs} className="w-full mt-4">
                      {isSaving ? "Speichert..." : "Punkteinstellungen Speichern"}
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Column 2: Rewards Overview (Placeholder for dynamic rewards) */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Award className="mr-2 h-5 w-5 text-green-500" />
                Prämien
              </CardTitle>
              <CardDescription>Erreichbare Meilensteine und Belohnungen.</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingRewards ? (
                <p>Lade Prämien...</p>
              ) : rewards.length > 0 ? (
                <ul className="space-y-2">
                  {rewards.map((reward) => (
                    <li key={reward.id} className="p-3 bg-gray-50 rounded-md shadow-sm">
                      <div className="flex items-center justify-between w-full">
                        {/* Left side: Icon and Name */}
                        <div className="flex items-center flex-1 min-w-0">
                          {reward.iconName && (
                            <div className="mr-3 flex-shrink-0">
                              {React.createElement(iconMap[reward.iconName] || DefaultRewardIcon, { className: "h-6 w-6 text-indigo-500" })}
                            </div>
                          )}
                          <span className="font-medium text-gray-800 truncate">{reward.name}</span>
                        </div>

                        {/* Right side: Points and Admin controls */}
                        <div className="flex items-center ml-4 flex-shrink-0">
                          <span className="text-sm font-semibold text-green-600 mr-3">{reward.pointsRequired} Pkt.</span>
                          {currentUser?.role === 'admin' && (
                            <div className="flex items-center space-x-0.5">
                              <Button variant="ghost" size="sm" onClick={() => openEditRewardDialog(reward)} title="Prämie bearbeiten" className="p-1 h-auto text-blue-600 hover:text-blue-700">
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleRewardDelete(reward.id)} title="Prämie löschen" className="p-1 h-auto text-red-600 hover:text-red-700">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500">Noch keine Prämien definiert.</p>
              )}
              {currentUser?.role === 'admin' && (
                <Button variant="outline" className="w-full mt-4" onClick={openAddRewardDialog}> {/* Activate button */}
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Prämie Hinzufügen
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Column 3: Top Scores */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Crown className="mr-2 h-5 w-5 text-amber-500" />
                Top Stats
              </CardTitle>
              <CardDescription>All-Time, Monatliche & Wöchentliche Bestleistungen.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-700">All-Time High Score:</h3>
                {isLoadingLeaderboard ? (
                  <p className="text-gray-600">Wird geladen...</p>
                ) : leaderboardData.length > 0 ? (
                  <div className="mt-1">
                    <p className="text-2xl font-bold text-amber-600">
                      {Math.max(...leaderboardData.map(score => score.allTimeHigh))} Pkt.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      von {leaderboardData.find(score => score.allTimeHigh === Math.max(...leaderboardData.map(s => s.allTimeHigh)))?.userName}
                    </p>
                  </div>
                ) : (
                  <p className="text-gray-600">Noch keine Daten</p>
                )}
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Bester des Monats (aktuell):</h3>
                {isLoadingLeaderboard ? (
                  <p className="text-gray-600">Wird geladen...</p>
                ) : leaderboardData.length > 0 ? (
                  <div className="mt-1">
                    {(() => {
                      const maxMonthPoints = Math.max(...leaderboardData.map(score => score.currentMonthPoints));
                      const topUser = leaderboardData.find(score => score.currentMonthPoints === maxMonthPoints);
                      return maxMonthPoints > 0 ? (
                        <>
                          <p className="text-2xl font-bold text-purple-600">
                            {maxMonthPoints} Pkt.
                          </p>
                          <p className="text-sm text-muted-foreground">
                            von {topUser?.userName}
                          </p>
                        </>
                      ) : (
                        <p className="text-lg text-gray-500">Noch keine Punkte diesen Monat</p>
                      );
                    })()}
                  </div>
                ) : (
                  <p className="text-gray-600">Noch keine Daten</p>
                )}
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Bester der Woche (aktuell):</h3>
                {isLoadingLeaderboard ? (
                  <p className="text-gray-600">Wird geladen...</p>
                ) : leaderboardData.length > 0 ? (
                  <div className="mt-1">
                    {(() => {
                      const maxWeekPoints = Math.max(...leaderboardData.map(score => score.currentWeekPoints));
                      const topUser = leaderboardData.find(score => score.currentWeekPoints === maxWeekPoints);
                      return maxWeekPoints > 0 ? (
                        <>
                          <p className="text-2xl font-bold text-green-600">
                            {maxWeekPoints} Pkt.
                          </p>
                          <p className="text-sm text-muted-foreground">
                            von {topUser?.userName}
                          </p>
                        </>
                      ) : (
                        <p className="text-lg text-gray-500">Noch keine Punkte diese Woche</p>
                      );
                    })()}
                  </div>
                ) : (
                  <p className="text-gray-600">Noch keine Daten</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Leaderboard Table */}
        <Card className="mt-0">
          <CardHeader>
            <CardTitle>Team Rangliste (Praktikanten)</CardTitle>
            <CardDescription>Übersicht der Punkte aller Praktikanten.</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingLeaderboard ? (
              <p>Lade Rangliste...</p>
            ) : leaderboardData.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">Rang</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Punkte Gesamt</TableHead>
                    <TableHead className="text-right">Punkte diese Woche</TableHead>
                    <TableHead className="text-right">Punkte diesen Monat</TableHead>
                    <TableHead className="text-right">All-Time High</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaderboardData.map((user, index) => (
                    <TableRow key={user.userId}>
                      <TableCell className="font-medium">{index + 1}</TableCell>
                      <TableCell>{user.userName}</TableCell>
                      <TableCell className="text-right font-semibold text-indigo-600">{user.totalPoints}</TableCell>
                      <TableCell className="text-right font-semibold text-green-600">{user.currentWeekPoints}</TableCell>
                      <TableCell className="text-right font-semibold text-purple-600">{user.currentMonthPoints}</TableCell>
                      <TableCell className="text-right font-semibold text-amber-600">{user.allTimeHigh}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-center text-gray-500 py-4">Noch keine Punktestände für Praktikanten vorhanden oder keine Praktikanten/Leads für die Berechnung gefunden.</p>
            )}
          </CardContent>
        </Card>

        {/* Reward Management Dialog */}
        {currentUser?.role === 'admin' && (
          <ManageRewardsDialog
            isOpen={isRewardDialogOpen}
            onClose={closeRewardDialog}
            onSubmit={handleRewardSubmit}
            rewardToEdit={editingReward}
          />
        )}

      </div>
    </div>
  );
} 
